/**
 * EditorTrustBadge — small status pill showing the editor's current
 * trust tier. Renders in the EditorPortal header so editors see
 * their standing at a glance + when they're close to graduation.
 *
 *   Novice  → "novice — X/50 approvals" (white pill)
 *   Trusted → "TRUSTED · low-risk auto-executes" (gold pill, Sparkles)
 *   Cooldown → "cooled down — back in N days" (amber pill, AlertTriangle)
 *
 * Click → opens a small tooltip with the policy explainer.
 */
import { useEffect, useState } from "react";
import { Sparkles, AlertTriangle, ShieldCheck } from "lucide-react";
import api from "@/lib/api";

function daysUntil(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 0;
  return Math.ceil(ms / 86400000);
}

export default function EditorTrustBadge({ className = "" }) {
  const [trust, setTrust] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/editor/trust")
      .then((res) => { if (!cancelled) setTrust(res.data); })
      .catch(() => { /* silent — editor may not have permission */ });
    return () => { cancelled = true; };
  }, []);

  if (!trust) return null;
  // Admins don't need a tier badge.
  if (trust.tier === "admin") return null;

  const cooldownDays = trust.cooldown_until ? daysUntil(trust.cooldown_until) : null;
  const inCooldown = cooldownDays !== null && cooldownDays > 0;
  const isTrusted = trust.tier === "trusted";

  const pillCls = inCooldown
    ? "bg-amber-500/15 text-amber-300 border-amber-500/30"
    : isTrusted
      ? "bg-gold/15 text-gold border-gold/40"
      : "bg-white/5 text-white/70 border-white/15";

  const Icon = inCooldown ? AlertTriangle : isTrusted ? Sparkles : ShieldCheck;

  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-1.5 px-3 py-1 text-[10px] font-body uppercase tracking-wider border transition-colors ${pillCls}`}
        data-testid="editor-trust-badge"
      >
        <Icon size={11} />
        {inCooldown
          ? <span data-testid="editor-trust-tier">cooled down · {cooldownDays}d left</span>
          : isTrusted
            ? <span data-testid="editor-trust-tier">trusted · low-risk auto-executes</span>
            : (
              <span data-testid="editor-trust-tier">
                novice · {trust.approved_count}/{trust.approved_count + trust.next_threshold} approvals
              </span>
            )}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 w-72 bg-luxury-charcoal/95 backdrop-blur-md border border-gold/30 shadow-xl p-4 z-50 text-white/70 text-[11px] font-body leading-relaxed" data-testid="editor-trust-tooltip">
          <p className="text-gold font-heading text-xs mb-2 tracking-wider">EDITOR TRUST TIERS</p>
          <p className="mb-2">
            <span className="text-white font-semibold">Novice</span> — All gated actions (gallery delete, bulk delete ≥ 5, event delete, photo replace) require admin approval.
          </p>
          <p className="mb-2">
            <span className="text-gold font-semibold">Trusted</span> — After 50 approvals + 0 rejections in 90 days, your LOW-risk gated actions execute instantly. MEDIUM and HIGH still need admin sign-off.
          </p>
          <p className="text-amber-300/80">
            One rejection drops you back to Novice with a 14-day cooldown.
          </p>
          <div className="border-t border-white/10 mt-3 pt-2 text-white/40">
            Approved {trust.approved_count} · Rejected {trust.rejected_count} · Window {trust.window_days}d
          </div>
        </div>
      )}
    </div>
  );
}
